// ---------------------------------------------------------------------------
// Accordion Renderer (editor key: Accordion)
// ---------------------------------------------------------------------------
// Renders a question/answer list as semantic `<details>` / `<summary>`
// elements. Editor input is a pipe-delimited textarea
// (`Question | Answer` per line). The browser handles open/close natively,
// so the SSR output is fully static — no client JS required.
//
// Distinct from `faq-accordion`: that block is a richer FAQ surface with
// SEO schema hooks; this block is the lightweight inline `<details>` group
// the editor surfaces under the Accordion Puck component.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text } from "../escape.js";

interface AccordionProps {
  heading?: string;
  items?: string;
  allowMultiple?: string;
}

interface AccordionItem {
  question: string;
  answer: string;
}

function parseItems(value: unknown): AccordionItem[] {
  if (typeof value !== "string") return [];
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const [q, a] = line.split("|").map((part) => part.trim());
      return {
        question: q ?? "",
        answer: a ?? "",
      };
    })
    .filter((entry) => entry.question.length > 0);
}

function renderItem(item: AccordionItem, index: number, sharedName: string): string {
  // `name=""` groups <details> elements into an exclusive set — only one
  // open at a time. When allowMultiple is "yes" we omit the attribute so
  // the browser allows several open simultaneously.
  const nameAttr = sharedName ? ` name="${attr(sharedName)}"` : "";
  // First item starts open so the block is not silent on page load.
  const openAttr = index === 0 ? " open" : "";
  const answerHtml = item.answer
    ? `\n      <p class="ease-accordion__answer">${text(item.answer)}</p>`
    : "";

  return `    <details class="ease-accordion__item"${nameAttr}${openAttr}>
      <summary class="ease-accordion__summary">${text(item.question)}</summary>${answerHtml}
    </details>`;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as AccordionProps;
  const items = parseItems(p.items);

  if (items.length === 0) {
    return "<!-- accordion missing items -->";
  }

  const heading = typeof p.heading === "string" ? p.heading.trim() : "";
  const allowMultiple = p.allowMultiple === "yes";
  const sharedName = allowMultiple ? "" : "ease-accordion-group";

  const headingHtml = heading
    ? `\n  <h2 class="ease-accordion__heading">${text(heading)}</h2>`
    : "";

  const body = items
    .map((item, index) => renderItem(item, index, sharedName))
    .join("\n");

  return `<section class="ease-section ease-accordion" data-allow-multiple="${attr(allowMultiple ? "yes" : "no")}">${headingHtml}
  <div class="ease-accordion__list">
${body}
  </div>
</section>`;
}

registerRenderer("accordion", { render });
